/**
 * @module gisw/Helpers/type
 * @description
 * a helper module for working with and determining types of objects.
 */
define([], function () {
  'use strict';

  return {
    /**
     * @function groupBy
     * @public
     * @memberOf module:gisw/Helpers/type
     *
     * @param  {Array} list
     * @param  {function (any): string} callback
     * return key used to store elem.
     */
    groupBy (list, callback) {
      /** @type {Record<string, Array>} */
      const results = {};

      for (const item of list) {
        const key = callback(item);

        if (!results.hasOwnProperty(key)) {
          results[key] = [];
        }

        results[key].push(item);
      }

      return results;
    },

    /**
     * @function forEach
     * @public
     * @memberOf module:gisw/Helpers/type
     * @description
     * normalizes looping through arrays and objects.  If parameter `value`
     * is any array, this function is equivalent to the usual `Array.forEach`
     * method with the addition that the (second) parameter `thisArg` defaults
     * to the array being looped over.  If `value` is an object, the `element`s
     * recieved in `callback` are obtained by looping through the keys of
     * `value` filtered using `hasOwnProperty`.
     *
     * @example
     * type.forEach(someObject, function(key) {
     *  // do something w/keys
     * });
     *
     * type.forEach(someArray, function(item, index, arr) {
       // the usual...
     * })
     *
     * @param  {Record<string, any>|Array} value
     * @param  {forEachCallback} callback
     * function to be executed for each key/index and value.
     * @param  {any} [thisArg=`value`]
     * sets the value of `this` in the callback function
     */
    forEach (value, callback, thisArg = value) {
      if (Array.isArray(value)) {
        value.forEach(callback);
      } else if (typeof value === 'object' && value !== null) {
        for (const key in value) {
          if (value.hasOwnProperty(key)) {
            callback.call(thisArg, value[key], key, value);
          }
        }
      }
    }
  };
});
/**
 * @callback forEachCallback
 * @description
 * In short, the parameters of this callback satisfy
 * `iterable[position] = element` when `value` is an array or object.
 * @param {object} element
 * If `value` is an array,
 * then this is element at the index given by `position`.
 *
 * If `value` is an object,
 * then this is the element whose key is `position`.
 * @param {number|string} position
 * The index/key for `element`
 * @param {array|object} iterable
 * The array/object being looped through, i.e. `value`.
 */
